import React, { useCallback } from "react";
import { Search } from "lucide-react";
import { Button } from "@mui/material";
import { DownloadAsExcelSheet } from "../DownloadAsExcelSheet";
import { EnquiryDateRangeFilterBar } from "./EnquiryDateRangeFilter";
import { ENQUIRY_EXPORT_COLUMNS, mapEnquiriesForExport } from "./enquiry-export";
import { hasActiveEnquiryFilters } from "./enquiry-filters";
import type { EnquiryDateRangeFilter } from "./enquiry-date-range";
import type { EnquiryFormFilter } from "./enquiry-filters";
import type { EnquiryRow } from "./enquiry-helpers";

type EnquiryToolbarProps = {
    query: string;
    formFilter: EnquiryFormFilter;
    dateRange: EnquiryDateRangeFilter;
    rows: EnquiryRow[];
    onQueryChange: (value: string) => void;
    onFormFilterChange: (value: EnquiryFormFilter) => void;
    onDateRangeChange: (value: EnquiryDateRangeFilter) => void;
    onClearFilters: () => void;
};

const FORM_OPTIONS: { value: EnquiryFormFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "services", label: "Services" },
    { value: "careers", label: "Careers" },
    { value: "leads", label: "Leads" },
];
type callBackFn = (...args: any) => void;

const debounce = (callBackFn: callBackFn, delay = 2000) => {
    let timout: ReturnType<typeof setTimeout>;
    return (...args: any) => {
        clearTimeout(timout);
        timout = setTimeout(() => callBackFn(...args), delay);
    };
};

const chipClass = (active: boolean) =>
    active
        ? "border-gray-900 bg-gray-900 text-white"
        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50";

export const EnquiryToolbar = ({
    query,
    formFilter,
    dateRange,
    rows,
    onQueryChange,
    onFormFilterChange,
    onDateRangeChange,
    onClearFilters,
}: EnquiryToolbarProps) => {
    const filtersActive = hasActiveEnquiryFilters(query, formFilter, dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedOnQueryChange = useCallback(
        debounce((value: string) => {
            onQueryChange(value); // Trigger your search/API call here
        }, 500),
        [] // Empty deps ensures function isn't recreated on every render
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // Update controlled input state immediately
        debouncedOnQueryChange(value); // Debounce the API search call
    };
    return (
        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="relative min-w-0 flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="search"
                        value={query}
                        onChange={handleInputChange}
                        placeholder="Search by name, email, phone..."
                        className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
                    />
                </div>
                <div className="flex shrink-0 items-center justify-end gap-2">
                    {filtersActive ? (
                        <Button
                            variant="text"
                            size="small"
                            onClick={onClearFilters}
                            sx={{ color: "#6B7280", textTransform: "none", fontWeight: 500 }}
                        >
                            Clear filters
                        </Button>
                    ) : null}
                    <DownloadAsExcelSheet
                        data={mapEnquiriesForExport(rows)}
                        cols={ENQUIRY_EXPORT_COLUMNS}
                        name="Rajneethi Enquiries"
                        variant="button"
                        buttonLabel="Export CSV"
                    />
                </div>
            </div>

            <div className="mt-4 grid gap-4 border-t border-gray-100 pt-4 lg:grid-cols-2">
                <div>
                    <p className="mb-2 text-xs font-medium text-gray-500">Form type</p>
                    <div className="flex flex-wrap gap-2">
                        {FORM_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => onFormFilterChange(option.value)}
                                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${chipClass(formFilter === option.value)}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                <EnquiryDateRangeFilterBar value={dateRange} onChange={onDateRangeChange} />
            </div>
        </div>
    );
};
